import {Subscription} from "rxjs";
import {RouterLink} from "@angular/router";
import {DatePipe, NgClass} from "@angular/common";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';

import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {OwnProfileResponse} from "@numizmat/services-models/core";
import {AlertService, ModalService} from "@numizmat/services/common";
import {ImageComponent, UserRatingComponent} from "@numizmat/components";
import {
  EmailModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/email-modal/email-modal.component";
import {
  PaymentModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/payment-modal/payment-modal.component";
import {
  PasswordModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/password-modal/password-modal.component";
import {
  UserDefaultAddressModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/user-default-address-modal/user-default-address-modal.component";
import {
  ProfileImageModalComponent
} from "@numizmat/pages/profile-page/components/own-profile/modals/profile-image-modal/profile-image-modal.component";

@Component({
  selector: 'nz-profile-dashboard',
  standalone: true,
  imports: [
    NgClass,
    DatePipe,
    UserRatingComponent,
    RouterLink,
    ImageComponent
  ],
  templateUrl: './profile-dashboard.component.html',
  styleUrl: './profile-dashboard.component.scss'
})
export class ProfileDashboardComponent implements OnInit, OnDestroy {
  @Input() profile!: OwnProfileResponse;

  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);

  private subs: Subscription = new Subscription();

  protected profileImageUrl!: string;

  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.initializeProfileImageUrl();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onOpenProfileImageModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(ProfileImageModalComponent, {
      scrollable: false
    });
    modalRef.componentInstance.profileImageUrl = this.profileImageUrl;
    const subscription: Subscription = modalRef.componentInstance.userProfileImageIdChange.subscribe({
      next: (userProfileImageId: string | undefined) => {
        if (!!userProfileImageId) {
          this.profile.userProfileImageId = userProfileImageId;
        } else {
          this.profile.userProfileImageId = undefined;
        }
        this.initializeProfileImageUrl();
      }
    });
    this.subs.add(subscription);
  }

  protected onOpenPasswordModal(): void {
    this.modalService.open(PasswordModalComponent, {
      scrollable: true
    });
  }

  protected onOpenEmailModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(EmailModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.currentEmail = this.profile.userEmail;
    const subscription: Subscription = modalRef.componentInstance.emailChange.subscribe({
      next: (newEmail: string) => {
        this.profile.userEmail = newEmail;
      }
    });
    this.subs.add(subscription);
  }

  protected onOpenPaymentModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(PaymentModalComponent, {
      scrollable: true
    });
    if (this.profile.currentBalance <= -1) {
      modalRef.componentInstance.amountInput = this.profile.currentBalance * -1;
    }
  }

  protected onOpenUserDefaultAddressModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(UserDefaultAddressModalComponent, {
      scrollable: false
    });
    modalRef.componentInstance.currentRegionId = this.profile.regionId;
    modalRef.componentInstance.currentMunicipalityId = this.profile.municipalityId;
    modalRef.componentInstance.currentPopulatedPlaceId = this.profile.populatedPlaceId;

    const subscription: Subscription = modalRef.componentInstance.addressChange.subscribe({
      next: (newAddress: any) => {
        this.profile.regionId = newAddress.regionId;
        this.profile.municipalityId = newAddress.municipalityId;
        this.profile.populatedPlaceId = newAddress.populatedPlaceId;
        this.profile.populatedPlaceName = newAddress.populatedPlaceName;
      }
    });
    this.subs.add(subscription);
  }

  protected showUserRatingInfo(): void {
    this.alertService.showUserRatingInfo();
  }

  private initializeProfileImageUrl(): void {
    if (!!this.profile.userProfileImageId) {
      this.profileImageUrl = `${environment.apiImageUserProfileUrl}/${this.profile.userId}/${this.profile.userProfileImageId}`;
    } else {
      this.profileImageUrl = environment.apiImageDefaultUserProfileUrl;
    }
  }
}
